/** @file Dopesheet groups of one animation: bones, slots, IK, deform, draw order and event lanes, in order. */
import { compareText } from '@pose2d/api/queries/compareText';
import { IK_CHANNELS } from '@pose2d/api/queries/ikChannels.data';
import { nameIn } from '@pose2d/api/queries/nameIn';
import type { TimelineChannelInput, TimelineGroupInput } from '@pose2d/editor-geometry/types';
import type { AnimationDoc, Doc, Id } from '@pose2d/format/types';
import { dopeKeys } from './dopeKeys';

const BONE_PROPS = ['rotation', 'x', 'y', 'scaleX', 'scaleY', 'shearX', 'shearY', 'inherit'];
const SLOT_PROPS = ['attachment', 'rgb', 'alpha', 'dark'];
const IK_PROPS = Object.keys(IK_CHANNELS);

type Keys = readonly { f: number }[] | undefined;
type Tracks = Readonly<Record<string, Keys>>;
const framesOf = (keys: Keys): number[] => (keys ?? []).map((k) => k.f);

/** One channel row per keyed property, in table order, named as animation.setKey names the channel. */
function channelsOf(track: Tracks | undefined, props: readonly string[], channelOf: (prop: string) => string): TimelineChannelInput[] {
  const keyed = props.filter((p) => (track?.[p]?.length ?? 0) > 0);
  return keyed.map((p) => ({ channel: channelOf(p), label: '', frames: framesOf(track?.[p]) }));
}

/** Bone ids parents first, siblings by siblingIndex (the order of the hierarchy). */
function hierarchyOrder(bones: Doc['bones']): Id[] {
  const kids = new Map<Id | null, Id[]>();
  for (const [boneId, bone] of Object.entries(bones)) {
    const list = kids.get(bone.parent);
    if (list === undefined) kids.set(bone.parent, [boneId]);
    else list.push(boneId);
  }
  const out: Id[] = [];
  const visit = (boneId: Id): void => {
    out.push(boneId);
    const children = [...(kids.get(boneId) ?? [])].sort((a, b) => (bones[a]?.siblingIndex ?? 0) - (bones[b]?.siblingIndex ?? 0));
    for (const child of children) visit(child);
  };
  for (const root of kids.get(null) ?? []) visit(root);
  return out;
}

/** Bones in hierarchy order, then slots in setup draw order: keyed ones plus the ones in `show` (the selection). */
function skeletonGroups(doc: Doc, anim: AnimationDoc, show: ReadonlySet<Id>): TimelineGroupInput[] {
  const bones = hierarchyOrder(doc.bones).filter((b) => anim.bones[b] !== undefined || show.has(b));
  const slots = doc.drawOrder.filter((s) => anim.slots[s] !== undefined || show.has(s));
  const same = (p: string): string => p;
  return [
    ...bones.map((b) => ({ target: b, label: nameIn(doc.bones)(b), channels: channelsOf(anim.bones[b], BONE_PROPS, same) })),
    ...slots.map((s) => ({ target: s, label: nameIn(doc.slots)(s), channels: channelsOf(anim.slots[s], SLOT_PROPS, same) })),
  ];
}

/** IK constraints by `order`, deform attachments by name. */
function constraintGroups(doc: Doc, anim: AnimationDoc): TimelineGroupInput[] {
  const ik = Object.keys(anim.ik).sort((a, b) => (doc.constraints[a]?.order ?? 0) - (doc.constraints[b]?.order ?? 0));
  const deform = Object.keys(anim.deform).filter((a) => (anim.deform[a]?.length ?? 0) > 0);
  deform.sort((a, b) => compareText(nameIn(doc.attachments)(a), nameIn(doc.attachments)(b)));
  return [
    ...ik.map((c) => ({ target: c, label: nameIn(doc.constraints)(c), channels: channelsOf(anim.ik[c], IK_PROPS, (p) => IK_CHANNELS[p] ?? p) })),
    ...deform.map((a) => ({ target: a, label: nameIn(doc.attachments)(a), channels: [{ channel: 'deform', label: '', frames: framesOf(anim.deform[a]) }] })),
  ];
}

/** The draw-order row and one lane per event (by event name); labels of these groups come from the panel. */
function sequenceGroups(doc: Doc, anim: AnimationDoc): TimelineGroupInput[] {
  const out: TimelineGroupInput[] = [];
  if (anim.drawOrder.length > 0) out.push({ target: dopeKeys.DRAW_ORDER, label: '', channels: [{ channel: 'drawOrder', label: '', frames: framesOf(anim.drawOrder) }] });
  const events = [...new Set(anim.events.map((e) => e.event))].sort((a, b) => compareText(nameIn(doc.events)(a), nameIn(doc.events)(b)));
  const lane = (e: Id): TimelineChannelInput => ({ channel: `event:${e}`, label: nameIn(doc.events)(e), frames: framesOf(anim.events.filter((k) => k.event === e)) });
  if (events.length > 0) out.push({ target: dopeKeys.EVENTS, label: '', channels: events.map(lane) });
  return out;
}

/** Groups for timelineLayout (empty for a missing animation); `show` adds unkeyed bones and slots (selected ones). */
export function dopesheetGroups(doc: Doc, animation: Id, show: ReadonlySet<Id>): TimelineGroupInput[] {
  const anim = doc.animations[animation];
  if (anim === undefined) return [];
  return [...skeletonGroups(doc, anim, show), ...constraintGroups(doc, anim), ...sequenceGroups(doc, anim)];
}
